import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Snapshots surface (T-0057 / E05-F01 + T-0059 / E05-F02).
 *
 * Three calls needed by the T-0097 engagement overview hero:
 *
 *   - listSnapshots(engagementId)  — GET /api/v1/engagements/:id/snapshots
 *     Renders the "Snapshots" card on the overview (latest 5; the prototype
 *     shows them with the git-branch icon + capture date + snapshot_type
 *     label).
 *
 *   - captureSnapshot(engagementId, input) — POST /api/v1/engagements/:id/
 *     snapshots, idempotency key supplied. Wired to the "Capture snapshot"
 *     button in the hero CTA. Defaults to a pulse snapshot of the contract
 *     module (the most common consultant action — matches the prototype's
 *     "Capture" button on the snapshots card).
 *
 *   - requestPdfRender(engagementId, snapshotId) — POST /api/v1/engagements/
 *     :id/reports/:snapshot_id/pdf. Wired to the "Generate PDF" button.
 *     Returns either 200 (render already complete; blob_uri populated) or
 *     202 (render in flight; consumer polls or refreshes).
 *
 * Note: the snapshot_type enum is `pulse | posture | readiness | engagement`
 * (`engagement` = the overview hero capture, which freezes the whole
 * overview picture) per
 * apps/api/src/services/snapshots.ts → ALLOWED_SNAPSHOT_TYPES.
 */

export const SnapshotTypeSchema = z.enum(['pulse', 'posture', 'readiness', 'engagement']);
export type SnapshotType = z.infer<typeof SnapshotTypeSchema>;

export const SnapshotListItemSchema = z.object({
  id: z.string().uuid(),
  snapshot_type: SnapshotTypeSchema,
  sequence_no: z.number(),
  captured_at: z.string(),
});
export type SnapshotListItem = z.infer<typeof SnapshotListItemSchema>;

export const SnapshotListSchema = z.object({
  items: z.array(SnapshotListItemSchema),
  next_cursor: z.string().nullable(),
});
export type SnapshotList = z.infer<typeof SnapshotListSchema>;

export interface ListSnapshotsOpts {
  snapshot_type?: SnapshotType;
  cursor?: string;
  limit?: number;
}

export function listSnapshots(
  engagementId: string,
  opts: ListSnapshotsOpts = {},
): Promise<SnapshotList> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/snapshots`,
    SnapshotListSchema,
    { query: opts as Record<string, string | number | boolean | undefined> },
  );
}

export function useSnapshotListQuery(
  engagementId: string | undefined,
  opts: ListSnapshotsOpts = {},
) {
  return useQuery({
    queryKey: ['snapshots', engagementId, opts] as const,
    queryFn: () => listSnapshots(engagementId!, opts),
    enabled: Boolean(engagementId),
  });
}

// ===========================================================================
// Snapshot detail (T-0101 / E05-F01 viewer)
// ===========================================================================

export const SnapshotDetailSchema = z.object({
  id: z.string().uuid(),
  /** The capture kinds above, or a report kind (`vendor_intelligence_report`, `health_readiness_report`, ...). */
  snapshot_type: z.string(),
  sequence_no: z.number(),
  frozen_state: z.unknown(),
  source_ref_summary: z.unknown(),
  size_bytes: z.number(),
  captured_at: z.string(),
  captured_by: z.string(),
  is_immutable: z.literal(true),
});
export type SnapshotDetail = z.infer<typeof SnapshotDetailSchema>;

/**
 * Read one frozen snapshot. The full `frozen_state` payload returns here,
 * not in the list — the snapshot viewer (T-0101) is the only consumer of
 * the heavyweight body.
 */
export function getSnapshot(engagementId: string, snapshotId: string): Promise<SnapshotDetail> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/snapshots/${encodeURIComponent(
      snapshotId,
    )}`,
    SnapshotDetailSchema,
  );
}

export function useSnapshotDetailQuery(
  engagementId: string | undefined,
  snapshotId: string | undefined,
) {
  return useQuery({
    queryKey: ['snapshot', engagementId, snapshotId] as const,
    queryFn: () => getSnapshot(engagementId!, snapshotId!),
    enabled: Boolean(engagementId && snapshotId),
  });
}

export const CaptureSnapshotResultSchema = z.object({
  id: z.string().uuid(),
  sequence_no: z.number(),
  snapshot_type: SnapshotTypeSchema,
  captured_at: z.string(),
  is_immutable: z.literal(true),
});
export type CaptureSnapshotResult = z.infer<typeof CaptureSnapshotResultSchema>;

export interface CaptureSnapshotInput {
  snapshot_type: SnapshotType;
  source_selector: {
    module: string;
    as_of?: string;
  };
}

export function captureSnapshot(
  engagementId: string,
  input: CaptureSnapshotInput,
  idempotencyKey?: string,
): Promise<CaptureSnapshotResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/snapshots`,
    CaptureSnapshotResultSchema,
    {
      method: 'POST',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useCaptureSnapshotMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      input,
      idempotencyKey,
    }: {
      input: CaptureSnapshotInput;
      idempotencyKey?: string;
    }) => captureSnapshot(engagementId!, input, idempotencyKey),
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['snapshots', engagementId] });
      }
    },
  });
}

// ===========================================================================
// Capture preview — what a capture will freeze + who it will notify.
// ===========================================================================

export const CapturePreviewSchema = z.object({
  register_entry_count: z.number(),
  next_sequence_no: z.number(),
  notify: z.object({ team_members: z.number(), client_viewers: z.number() }),
  queues_ai_reevaluation: z.boolean(),
});
export type CapturePreview = z.infer<typeof CapturePreviewSchema>;

/**
 * Read-only preview backing the capture confirmation modal. Only fetched
 * while the modal is open (`enabled`), and never cached across opens —
 * the recipient counts must be current when the user confirms.
 */
export function useCapturePreviewQuery(engagementId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['snapshots', engagementId, 'capture-preview'] as const,
    queryFn: () =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/snapshots/capture-preview`,
        CapturePreviewSchema,
      ),
    enabled: Boolean(engagementId) && enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

// ===========================================================================
// PDF rendering — T-0059 / E05-F02
// ===========================================================================

export const PdfRenderResponseSchema = z.object({
  pdf_report_id: z.string().uuid(),
  render_status: z.string(),
  brand_template_version: z.string(),
  page_size: z.string(),
  blob_uri: z.string().nullable(),
  sha256: z.string().nullable(),
  size_bytes: z.number().nullable(),
  rendered_at: z.string().nullable(),
});
export type PdfRenderResponse = z.infer<typeof PdfRenderResponseSchema>;

/**
 * T-0115: GET-shape DTO for the PDF preview modal. The backend returns
 * the snapshot's *latest* pdf_reports row when one exists. ADR-009
 * guarantees the row is immutable once `render_status === 'succeeded'`;
 * a re-render creates a NEW row, the existing one stays frozen.
 *
 * Optional fields (download_url, renderer_kind, font_bundle_version,
 * etc.) are returned by the backend but may be null while the render is
 * still in flight.
 */
export const PdfReportDetailSchema = z.object({
  pdf_report_id: z.string().uuid(),
  snapshot_id: z.string().uuid(),
  render_status: z.string(),
  brand_template_version: z.string(),
  page_size: z.string(),
  blob_uri: z.string().nullable(),
  download_url: z.string().nullable(),
  render_error: z.string().nullable(),
  renderer_kind: z.string().nullable(),
  font_bundle_version: z.string().nullable(),
  size_bytes: z.number().nullable(),
  sha256: z.string().nullable(),
  rendered_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type PdfReportDetail = z.infer<typeof PdfReportDetailSchema>;

export function getPdfReport(engagementId: string, snapshotId: string): Promise<PdfReportDetail> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/reports/${encodeURIComponent(
      snapshotId,
    )}/pdf`,
    PdfReportDetailSchema,
  );
}

export function usePdfReportQuery(
  engagementId: string | undefined,
  snapshotId: string | undefined,
  opts: { pollMs?: number; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['pdf-report', engagementId, snapshotId] as const,
    queryFn: () => getPdfReport(engagementId!, snapshotId!),
    enabled: Boolean(engagementId) && Boolean(snapshotId) && (opts.enabled ?? true),
    refetchInterval: opts.pollMs ?? false,
  });
}

export interface RequestPdfRenderInput {
  brand_template_version?: string;
  page_size?: 'us-letter' | 'a4';
}

export function requestPdfRender(
  engagementId: string,
  snapshotId: string,
  input: RequestPdfRenderInput = {},
  idempotencyKey?: string,
): Promise<PdfRenderResponse> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/reports/${encodeURIComponent(
      snapshotId,
    )}/pdf`,
    PdfRenderResponseSchema,
    {
      method: 'POST',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useRequestPdfRenderMutation(engagementId: string | undefined) {
  return useMutation({
    mutationFn: ({
      snapshotId,
      input,
      idempotencyKey,
    }: {
      snapshotId: string;
      input?: RequestPdfRenderInput;
      idempotencyKey?: string;
    }) => requestPdfRender(engagementId!, snapshotId, input ?? {}, idempotencyKey),
  });
}
